import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Button, DateTimePicker } from '../../../components/ui';

/**
 * Step 4: Schedule
 *
 * End date and minimum threshold.
 */
export default function StepSchedule({ onNext, onBack }) {
  const {
    watch,
    setValue,
    formState: { errors },
    trigger,
  } = useFormContext();

  const endsAt = watch('endsAt');
  const minimumThreshold = watch('minimumThreshold');
  const ticketPrice = watch('ticketPrice');
  const ticketQuantity = watch('ticketQuantity');

  // Calculate earnings based on threshold (5% platform fee)
  const PLATFORM_FEE_PERCENT = 5;
  const calculateEarnings = (percentSold) => {
    const ticketsSold = Math.floor((ticketQuantity * percentSold) / 100);
    const totalRevenue = ticketsSold * ticketPrice; // in cents
    const platformFee = Math.round(totalRevenue * (PLATFORM_FEE_PERCENT / 100));
    const sellerEarnings = totalRevenue - platformFee;
    return {
      ticketsSold,
      totalRevenue: totalRevenue / 100, // convert to euros
      platformFee: platformFee / 100,
      sellerEarnings: sellerEarnings / 100,
    };
  };

  const thresholdEarnings = calculateEarnings(minimumThreshold);
  const fullSaleEarnings = calculateEarnings(100);

  // Calculate days remaining (rounded to nearest day for comparison)
  const daysRemaining = endsAt
    ? Math.round((new Date(endsAt) - new Date()) / (1000 * 60 * 60 * 24))
    : 0;

  // Track which duration button was clicked
  const [selectedDuration, setSelectedDuration] = useState(null);

  const isValid = endsAt && new Date(endsAt) > new Date() && minimumThreshold >= 50 && minimumThreshold <= 100;

  async function handleContinue() {
    const valid = await trigger(['endsAt', 'minimumThreshold']);
    if (valid) {
      onNext();
    }
  }

  function handleDateChange(date) {
    setValue('endsAt', date, { shouldValidate: true });
  }

  // Quick duration buttons
  const durations = [
    { label: '3 days', days: 3 },
    { label: '7 days', days: 7 },
    { label: '14 days', days: 14 },
    { label: '30 days', days: 30 },
  ];

  function setDuration(days) {
    const date = new Date();
    // For 30 days, set to 29 days + current time to avoid exceeding max
    if (days === 30) {
      date.setDate(date.getDate() + 29);
      date.setHours(23, 59, 0, 0); // End of day 29 ≈ 30 days
    } else {
      date.setDate(date.getDate() + days);
      date.setHours(20, 0, 0, 0); // Default to 8 PM
    }
    setSelectedDuration(days);
    setValue('endsAt', date, { shouldValidate: true });
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-display text-cream mb-2">
          Schedule Your Competition
        </h1>
        <p className="text-cream/60">
          Choose when your competition ends and set minimum entry sales.
        </p>
      </div>

      {/* Form */}
      <div className="space-y-6 mb-8">
        {/* End Date */}
        <DateTimePicker
          label="End Date & Time"
          value={endsAt}
          onChange={handleDateChange}
          minDate={new Date(Date.now() + 24 * 60 * 60 * 1000)}
          maxDate={new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)}
          helperText="1 - 30 days from now"
          error={errors.endsAt?.message}
        />

        {/* Quick Duration Buttons */}
        <div>
          <label className="block text-xs font-medium text-cream/70 uppercase tracking-wider mb-3">
            Quick Select
          </label>
          <div className="flex flex-wrap gap-2">
            {durations.map(({ label, days }) => (
              <button
                key={days}
                type="button"
                onClick={() => setDuration(days)}
                className={`px-4 py-2.5 rounded-lg border font-mono font-semibold transition-all ${
                  selectedDuration === days
                    ? 'border-primary bg-primary/20 text-primary shadow-sm shadow-primary/20'
                    : 'border-midnight-lighter bg-midnight text-cream/60 hover:border-primary/30 hover:text-cream'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Minimum Threshold */}
        <div>
          <label className="block text-sm font-medium text-cream mb-2">
            Minimum Sales Threshold
          </label>
          <p className="text-cream/50 text-sm mb-4">
            If this percentage of entries isn't sold by the end date, all buyers are refunded.
          </p>
          <div className="flex items-center gap-4">
            <input
              type="range"
              min="50"
              max="100"
              step="5"
              value={minimumThreshold}
              onChange={(e) => setValue('minimumThreshold', parseInt(e.target.value), { shouldValidate: true })}
              className="flex-1 h-2 bg-midnight-lighter rounded-lg appearance-none cursor-pointer accent-primary"
            />
            <span className="text-cream font-semibold w-14 text-right">{minimumThreshold}%</span>
          </div>
          <div className="flex justify-between text-xs text-cream/40 mt-1">
            <span>50% (easier)</span>
            <span>100% (all entries)</span>
          </div>
        </div>

        {/* Earnings Preview */}
        <div className="bg-gradient-to-br from-primary/10 to-accent/10 border border-primary/20 rounded-xl p-5">
          <h3 className="font-semibold text-cream mb-4 flex items-center gap-2">
            <span className="text-lg">💰</span>
            Your Potential Earnings
          </h3>
          <div className="space-y-3">
            {/* Threshold earnings */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-cream font-medium">If {minimumThreshold}% sells</p>
                <p className="text-cream/50 text-sm">{thresholdEarnings.ticketsSold} entries</p>
              </div>
              <div className="text-right">
                <p className="text-xl font-bold text-primary">€{thresholdEarnings.sellerEarnings.toFixed(2)}</p>
              </div>
            </div>

            {/* Divider */}
            {minimumThreshold < 100 && (
              <>
                <div className="border-t border-midnight-lighter" />
                {/* Full sale earnings */}
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-cream font-medium">If 100% sells</p>
                    <p className="text-cream/50 text-sm">{fullSaleEarnings.ticketsSold} entries</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold text-accent">€{fullSaleEarnings.sellerEarnings.toFixed(2)}</p>
                  </div>
                </div>
              </>
            )}
          </div>
          <p className="text-cream/40 text-xs mt-4">
            5% platform fee covers payment processing, escrow & dispute handling. Free to list.
          </p>
        </div>

        {/* Info Box */}
        <div className="bg-midnight-light border border-midnight-lighter rounded-xl p-5">
          <h3 className="font-semibold text-cream mb-3 flex items-center gap-2">
            <span className="text-lg">💡</span>
            How it works
          </h3>
          <ul className="space-y-2 text-cream/60 text-sm">
            <li className="flex items-start gap-2">
              <span className="text-primary">•</span>
              Your competition runs until the end date you set
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">•</span>
              If {minimumThreshold}% of entries are sold, a winner is drawn automatically
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">•</span>
              If the threshold isn't met, all entries are refunded
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">•</span>
              Lower thresholds are more likely to succeed but may earn less
            </li>
          </ul>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between gap-4">
        <Button variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button onClick={handleContinue} disabled={!isValid}>
          Continue
        </Button>
      </div>
    </div>
  );
}
